import React from 'react'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'

/**
 * Reusable Select component with consistent styling
 *
 * @param {string} label - Input label
 * @param {any} value - Current selected value
 * @param {function} onChange - Change handler (receives value, not event)
 * @param {array} options - Array of options: [{value, label}] or primitive values
 * @param {string} name - Optional name attribute
 * @param {boolean} disabled - Whether the select is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} error - Error message to display
 * @param {string} helperText - Helper text to display below
 * @param {string} placeholder - Placeholder text
 * @param {boolean} fullWidth - Whether to use full width
 * @param {object} sx - Additional MUI sx styles
 */
const SelectField = ({
    label,
    value,
    onChange,
    options = [],
    name,
    disabled = false,
    required = false,
    error = '',
    helperText = '',
    placeholder = '',
    fullWidth = false,
    sx = {},
    ...props
}) => {
    const handleChange = (event) => {
        if (onChange) {
            onChange(event.target.value, event)
        }
    }

    // Normalize options to [{value, label}] format - filter out invalid options
    const normalizedOptions = (options || [])
        .filter(opt => opt !== null && opt !== undefined)
        .map(opt => {
            if (typeof opt === 'object') {
                const val = opt.value ?? opt.id ?? opt._id
                // Skip options with undefined/null values
                if (val === undefined || val === null) return null
                return {
                    value: val,
                    label: opt.label ?? opt.name ?? opt.description ?? String(val)
                }
            }
            return { value: opt, label: String(opt) }
        })
        .filter(opt => opt !== null) // Remove any nulls from failed conversions

    const labelId = `${name || label}-label`
    const hasError = Boolean(error)

    return (
        <FormControl
            size="small"
            fullWidth={fullWidth}
            error={hasError}
            disabled={disabled}
            required={required}
            sx={{ minWidth: 120, ...sx }}
        >
            {label && (
                <InputLabel id={labelId}>{label}</InputLabel>
            )}
            <Select
                labelId={labelId}
                id={name}
                name={name}
                value={value !== undefined && value !== null ? value : ''}
                label={label}
                onChange={handleChange}
                displayEmpty={Boolean(placeholder)}
                {...props}
            >
                {placeholder && (
                    <MenuItem value="" disabled>
                        <em>{placeholder}</em>
                    </MenuItem>
                )}
                {normalizedOptions.map((option, index) => {
                    // Extra safety check - skip rendering if value is still undefined
                    if (option.value === undefined || option.value === null) return null
                    return (
                        <MenuItem key={`${option.value}-${index}`} value={option.value}>
                            {option.label}
                        </MenuItem>
                    )
                })}
            </Select>
            {(error || helperText) && (
                <FormHelperText>{error || helperText}</FormHelperText>
            )}
        </FormControl>
    )
}

export default SelectField
